/**
 * ProtectedRoute — the client half of SEC-01.
 *
 * Two different failures that look the same in most apps are kept apart here:
 * a missing session is a redirect, a missing permission is a panel. Silently
 * bouncing a signed-in user off a screen reads as a bug and generates a support
 * call; telling them their role does not cover it does not. The API enforces the
 * same rules again in PermissionsGuard — this only decides what to draw.
 */

import type { ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Card, EmptyState } from '@/components/ui';

export interface ProtectedRouteProps {
  /** SEC-01 — the user must hold **every** one of these permission keys. */
  permissions?: string[];
  /** SEC-01 — holding **any one** of these is enough. Combined with `permissions` by AND. */
  anyOf?: string[];
  /** Render these instead of the nested `<Outlet />` — for guarding a subtree inline. */
  children?: ReactNode;
}

export function ProtectedRoute({ permissions, anyOf, children }: ProtectedRouteProps) {
  const location = useLocation();
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const can = useAuthStore((state) => state.can);
  const canAny = useAuthStore((state) => state.canAny);

  // App's session bootstrap guarantees a hydrated user behind a live token, so a
  // missing one here means the session died mid-navigation — treat it as signed out.
  if (!accessToken || !user) {
    // `from` carries the attempted path so login lands the user where they were
    // going, not on a dashboard they then have to navigate away from.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const allowed =
    (!permissions?.length || can(...permissions)) && (!anyOf?.length || canAny(...anyOf));

  if (!allowed) return <ForbiddenPanel />;

  return <>{children ?? <Outlet />}</>;
}

function ForbiddenPanel() {
  const { t } = useTranslation('auth');

  return (
    <div className="flex h-full items-start justify-center p-4" data-testid="forbidden-panel">
      <Card className="w-full max-w-md">
        <EmptyState
          icon={ShieldAlert}
          title={t('guard.forbiddenTitle')}
          description={t('guard.forbiddenBody')}
          action={
            // A link, not a redirect: if the dashboard is out of reach too, the
            // user stays put and reads the message instead of ping-ponging.
            <Link to="/dashboard" className="btn-secondary mt-1">
              {t('guard.backToDashboard')}
            </Link>
          }
        />
      </Card>
    </div>
  );
}
